import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { getPortalHomePath } from './utils/portalPaths';
import ScrollRestoration from './components/ScrollRestoration';

import HomePage from './pages/HomePage';
import AboutPage from './pages/AboutPage';
import ProgramPage from './pages/ProgramPage';
import SubjectPage from './pages/SubjectPage';
import HowItWorksPage from './pages/HowItWorksPage';
import PricingPage from './pages/PricingPage';
import SuccessStoriesPage from './pages/SuccessStoriesPage';
import FaqPage from './pages/FaqPage';
import BlogPage from './pages/BlogPage';
import BlogPostPage from './pages/BlogPostPage';
import ContactPage from './pages/ContactPage';
import ServicesPage from './pages/ServicesPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import RefundCancellationPolicyPage from './pages/RefundCancellationPolicyPage';
import ServiceDeliveryPolicyPage from './pages/ServiceDeliveryPolicyPage';
import TermsConditionsPage from './pages/TermsConditionsPage';
import CheckoutPage from './pages/CheckoutPage';
import PaymentSuccessPage from './pages/PaymentSuccessPage';
import PaymentFailedPage from './pages/PaymentFailedPage';
import LoginRoute from './pages/LoginRoute';
import SignUpPage from './pages/SignUpPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import Dashboard from './pages/Dashboard';
import BillingPage from './pages/BillingPage';
import PaymentsPage from './pages/PaymentsPage';
import PaymentConfirmationPage from './pages/PaymentConfirmationPage';
import EditProfilePage from './pages/EditProfilePage';
import CoursePage from './pages/CoursePage';
import AdminPage from './pages/AdminPage';
import ExamsPage from './pages/ExamsPage';
import ExamPage from './pages/ExamPage';
import MyProgressPage from './pages/MyProgressPage';
import MessagesPage from './pages/MessagesPage';
import PortalBlockedPage from './pages/PortalBlockedPage';
import DevicePendingPage from './pages/DevicePendingPage';
import { StudentPortalGuard } from './components/student/StudentPortalGuard';

/** Logged-in students only — admins are sent to the admin console */
function StudentRoute({ children }) {
  const { currentUser, isAdmin } = useAuth();
  if (!currentUser) return <Navigate to="/login" replace />;
  if (isAdmin) return <Navigate to="/admin" replace />;
  return <StudentPortalGuard>{children}</StudentPortalGuard>;
}

function StudentPaidRoute({ children }) {
  const { currentUser, isAdmin, hasSubscription } = useAuth();
  if (!currentUser) return <Navigate to="/login" replace />;
  if (isAdmin) return <Navigate to="/admin" replace />;
  if (!hasSubscription) return <Navigate to="/billing" replace />;
  return children;
}

function PublicOnlyRoute({ children }) {
  const { currentUser, isAdmin } = useAuth();
  if (!currentUser) return children;
  return <Navigate to={getPortalHomePath({ isAdmin })} replace />;
}

function AdminRoute({ children }) {
  const { currentUser, isAdmin } = useAuth();
  if (!currentUser) return <Navigate to="/login" replace />;
  if (!isAdmin) return <Navigate to="/dashboard" replace />;
  return children;
}

function PortalEntryRedirect() {
  const { isAdmin } = useAuth();
  return <Navigate to={getPortalHomePath({ isAdmin })} replace />;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<HomePage />} />
      <Route path="/about" element={<AboutPage />} />
      <Route path="/program" element={<ProgramPage />} />
      <Route path="/program/:slug" element={<SubjectPage />} />
      <Route path="/how-it-works" element={<HowItWorksPage />} />
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/success-stories" element={<SuccessStoriesPage />} />
      <Route path="/faq" element={<FaqPage />} />
      <Route path="/blog" element={<BlogPage />} />
      <Route path="/blog/:slug" element={<BlogPostPage />} />
      <Route path="/contact" element={<ContactPage />} />
      <Route path="/services" element={<ServicesPage />} />
      <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
      <Route path="/refund-cancellation-policy" element={<RefundCancellationPolicyPage />} />
      <Route path="/service-delivery-policy" element={<ServiceDeliveryPolicyPage />} />
      <Route path="/terms-and-conditions" element={<TermsConditionsPage />} />
      <Route path="/checkout" element={<CheckoutPage />} />
      <Route path="/payment-success" element={<PaymentSuccessPage />} />
      <Route path="/payment-failed" element={<PaymentFailedPage />} />
      <Route
        path="/login"
        element={
          <PublicOnlyRoute>
            <LoginRoute />
          </PublicOnlyRoute>
        }
      />
      <Route
        path="/signup"
        element={
          <PublicOnlyRoute>
            <SignUpPage />
          </PublicOnlyRoute>
        }
      />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/portal-blocked" element={<PortalBlockedPage />} />
      <Route path="/device-pending" element={<DevicePendingPage />} />
      <Route path="/subscription-pending" element={<Navigate to="/billing" replace />} />
      <Route path="/portal" element={<PortalEntryRedirect />} />
      <Route
        path="/dashboard"
        element={
          <StudentRoute>
            <Dashboard />
          </StudentRoute>
        }
      />
      <Route
        path="/billing"
        element={
          <StudentRoute>
            <BillingPage />
          </StudentRoute>
        }
      />
      <Route
        path="/payments"
        element={
          <StudentPaidRoute>
            <PaymentsPage />
          </StudentPaidRoute>
        }
      />
      <Route
        path="/payment-confirmation/:paymentId"
        element={
          <StudentRoute>
            <PaymentConfirmationPage />
          </StudentRoute>
        }
      />
      <Route
        path="/profile"
        element={
          <StudentRoute>
            <EditProfilePage />
          </StudentRoute>
        }
      />
      <Route
        path="/course"
        element={
          <StudentPaidRoute>
            <CoursePage />
          </StudentPaidRoute>
        }
      />
      <Route
        path="/admin"
        element={
          <AdminRoute>
            <AdminPage />
          </AdminRoute>
        }
      />
      <Route
        path="/exams"
        element={
          <StudentPaidRoute>
            <ExamsPage />
          </StudentPaidRoute>
        }
      />
      <Route path="/exam/:subject" element={<Navigate to="/exams" replace />} />
      <Route
        path="/exam/:subject/:examId"
        element={
          <StudentPaidRoute>
            <ExamPage />
          </StudentPaidRoute>
        }
      />
      <Route
        path="/progress"
        element={
          <StudentPaidRoute>
            <MyProgressPage />
          </StudentPaidRoute>
        }
      />
      <Route
        path="/messages"
        element={
          <StudentPaidRoute>
            <MessagesPage />
          </StudentPaidRoute>
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <ScrollRestoration />
        <AppRoutes />
      </AuthProvider>
    </BrowserRouter>
  );
}
